'use client'

import { GOD } from 'api/admins'
import { IconCustomGoldPremium } from 'components/icons'
import { useTranslation } from 'components/providers/TranslationProvider'
import {
    DuoChartBar1,
    DuoCompetitionStage,
    DuoDoorOpen,
    DuoGlobe,
    DuoLayoutArrange,
    DuoLayoutTopPanel5,
    DuoSettings1,
    DuoUser,
    DuoUserTeam
} from 'components/icons/duotone'
import Link from 'components/shared/link'
import LanguageSwitcher from 'layout/Language/LanguageSwitcher'
import { X } from 'lucide-react'
import { handleLogout } from 'app/utils/logout'
import { ReactNode, useState } from 'react'
import { useParams, usePathname } from 'next/navigation'
import { extractLangFromPath } from 'app/lib/extractLangFromPath'
import { FaPhoneAlt } from 'react-icons/fa'
import { HiOutlineMenuAlt3 } from 'react-icons/hi'
import { IoInformationCircle, IoPerson } from 'react-icons/io5'
import { Avatar, AvatarImage } from 'ui/avatar'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from 'ui/sheet'

interface RightNavbarSliderMenuProps {
    user: IUser | null
    isHostDomain: boolean
    portalContainer?: HTMLElement | null
    /** When provided, used for admin link visibility. Otherwise only GOD (user.role) is used. */
    hasAdminRole?: boolean
}

const RightNavbarSliderMenu: React.FC<RightNavbarSliderMenuProps> = ({
    user,
    isHostDomain,
    portalContainer,
    hasAdminRole
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { t, isLoading } = useTranslation()

    if (isLoading || !t) return null

    const texts: RightNavTexts = {
        explore: t('explore') ?? 'Explore',
        organisations: t('organisations') ?? 'Organisations',
        federations: t('federations') ?? 'Federations',
        competitions: t('competitions') ?? 'Competitions',
        teams: t('teams') ?? 'Teams',
        admin: t('admin') ?? 'Admin',
        logout: t('logout') ?? 'Logout',
        login: t('login') ?? 'Login',
        signup: t('signup') ?? 'Sign up',
        playerHub: t('home') ?? 'Home',
        settings: t('profile') ?? 'Profile',
        premium: t('premium') ?? 'Premium',
        aboutUs: t('aboutUs') ?? 'About us',
        contactUs: t('contactUs') ?? 'Contact us',
        rankings: t('rankings') ?? 'Rankings'
    }

    const handleLinkClick = () => {
        setIsOpen(false)
    }

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
                <button
                    type='button'
                    className='flex items-center justify-center p-1.5 md:p-2 rounded-md hover:bg-muted/50 focus:outline-none cursor-pointer -mr-0.5 md:mr-0'
                    aria-label='Open menu'
                >
                    {!isOpen && <HiOutlineMenuAlt3 className='size-6 md:size-8' />}
                </button>
            </SheetTrigger>
            <SheetContent
                removeCloseButton
                className={`flex z-[999] !px-0 flex-col justify-between border-none w-full h-full gradient_bg`}
                side='right'
                portalContainer={portalContainer ?? undefined}
                positionStrategy={portalContainer ? 'absolute' : 'fixed'}
            >
                <SheetTitle className='sr-only'>Right slider</SheetTitle>
                <div className='w-full h-full relative px-6  overflow-x-hidden pb-12'>
                    <div className='flex justify-between items-center sticky top-0'>
                        <div onClick={handleLinkClick}>
                            <Link href='/explore/competitions' className='w-full'>
                                <div className='flex items-center gap-2 h-12 px-3 text-sm text-center font-medium md:mb-0 hover:bg-[rgba(70,118,216)]/[.16] hover:zinc-50 rounded-md transition-all duration-500 cursor-pointer hover:font-semibold w-full justify-between'>
                                    <p className='text-lg font-manrope whitespace-nowrap text-foreground group-hover:text-primary'>
                                        {texts.explore}
                                    </p>
                                </div>
                            </Link>
                        </div>
                        <X onClick={() => setIsOpen(false)} className='size-6' />
                    </div>
                    <div className='flex-col items-center gap-3 flex justify-center mt-8 md:mt-10'>
                        <div className='flex flex-col gap-5 w-full'>
                            <ExploreMenu texts={texts} isHostDomain={isHostDomain} onLinkClick={handleLinkClick} />
                            {user && <UserInfo user={user} texts={texts} onLinkClick={handleLinkClick} hasAdminRole={hasAdminRole} />}
                            <AuthButtons
                                user={user}
                                texts={texts}
                                onLinkClick={handleLinkClick}
                                isHostDomain={isHostDomain}
                            />
                            <LanguageSwitcher
                                triggerClassName={`${isHostDomain ? ' !bg-[#2d3b5c]' : '!bg-accent'} border-zinc-500 backdrop-blur-md h-10`}
                                contentClassName={`${isHostDomain ? 'border-zinc-500' : 'border-border'} bg-transparent backdrop-blur-md`}
                                hoverBackgroundClassName='hover:!bg-[rgba(70,118,216)]/[.24]'
                                selectedBackgroundClassName='bg-[rgba(70,118,216)]/[.16] focus:bg-[rgba(70,118,216)]/[.16] hover:bg-[rgba(70,118,216)]/[.16]'
                            />
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    )
}

interface ExploreNavSelectCardProps {
    icon: ReactNode
    value: string
    selected: boolean
    hover: boolean
    link: string
    onLinkClick: () => void
}

const ExploreNavSelectCard = ({ icon, value, selected, hover, link, onLinkClick }: ExploreNavSelectCardProps) => {
    const bgColor = selected
        ? 'bg-gradient-to-r from-[#3A8DD6] to-[#4676D8]'
        : hover
          ? 'bg-[rgba(70,118,216)]/[.24]'
          : 'bg-[rgba(70,118,216)]/[.16]'

    return (
        <div className='flex items-center gap-2.5' onClick={onLinkClick}>
            <span className={`rounded-md p-1 ${bgColor}`}>{icon}</span>
            <div className='flex flex-col'>
                <Link href={link} className={`text-sm ${selected ? 'font-semibold' : 'font-medium'}`}>
                    {value}
                </Link>
            </div>
        </div>
    )
}

interface ExploreMenuProps {
    texts: RightNavTexts
    isHostDomain: boolean
    onLinkClick: () => void
}

interface ExploreItemProps {
    selected: boolean
    hover: boolean
    texts: RightNavTexts
    onLinkClick: () => void
}

const ExploreMenu = ({ texts, isHostDomain, onLinkClick }: ExploreMenuProps) => {
    const exploreItems = [
        {
            value: 'Federations',
            description: ({ selected, hover, texts, onLinkClick }: ExploreItemProps) => (
                <ExploreNavSelectCard
                    link='/explore/federations'
                    icon={<DuoGlobe className='size-6' />}
                    value={texts.federations}
                    selected={selected}
                    hover={hover}
                    onLinkClick={onLinkClick}
                />
            )
        },
        {
            value: 'Competitions',
            description: ({ selected, hover, texts, onLinkClick }: ExploreItemProps) => (
                <ExploreNavSelectCard
                    link='/explore/competitions'
                    icon={<DuoCompetitionStage className='size-6' />}
                    value={texts.competitions}
                    selected={selected}
                    hover={hover}
                    onLinkClick={onLinkClick}
                />
            )
        },
        {
            value: 'Teams',
            description: ({ selected, hover, texts, onLinkClick }: ExploreItemProps) => (
                <ExploreNavSelectCard
                    link='/explore/teams'
                    icon={<DuoUserTeam className='size-6' />}
                    value={texts.teams}
                    selected={selected}
                    hover={hover}
                    onLinkClick={onLinkClick}
                />
            )
        },
        {
            value: 'Teams',
            description: ({ selected, hover, texts, onLinkClick }: ExploreItemProps) => (
                <ExploreNavSelectCard
                    link='/rankings'
                    icon={<DuoChartBar1 className='size-6' />}
                    value={texts.rankings}
                    selected={selected}
                    hover={hover}
                    onLinkClick={onLinkClick}
                />
            )
        }
    ]

    if (isHostDomain)
        exploreItems.unshift({
            value: 'Organisations',
            description: ({ selected, hover, texts, onLinkClick }: ExploreItemProps) => (
                <ExploreNavSelectCard
                    link='/explore/organisations'
                    icon={<DuoLayoutTopPanel5 className='size-6' />}
                    value={texts.organisations}
                    selected={selected}
                    hover={hover}
                    onLinkClick={onLinkClick}
                />
            )
        })

    return (
        <div className='w-full h-full gap-1 relative flex flex-col'>
            <div className='ml-8 w-full rounded-lg'>
                <div className='flex flex-col gap-4'>
                    {exploreItems.map((item, index) => (
                        <div key={index} onClick={onLinkClick}>
                            {item.description({ selected: false, hover: false, texts, onLinkClick })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

const UserInfo = ({
    user,
    texts,
    onLinkClick,
    hasAdminRole
}: {
    user: IUser
    texts: RightNavTexts
    onLinkClick: () => void
    hasAdminRole?: boolean
}) => {
    const isAdmin = hasAdminRole ?? user.role === GOD
    return (
        <div className='flex w-full relative flex-col gap-y-1'>
            <div className='w-full flex justify-between items-center cursor-pointer' onClick={onLinkClick}>
                <SheetTrigger asChild>
                    <Link
                        href='/home'
                        className='flex justify-start items-center gap-2 px-3 md:px-0 h-full text-sm font-semibold md:mb-0'
                    >
                        <Avatar className='rounded-md border'>
                            {user?.logo ? (
                                <AvatarImage src={user.logo} alt='user' className='border-2 rounded-md' />
                            ) : (
                                <IoPerson className='size-6 self-center mx-auto flex' />
                            )}
                        </Avatar>
                        <div className='flex flex-col gap-y-0.5'>
                            <div className='flex items-center gap-1'>
                                <p className='text-sm font-manrope font-semibold whitespace-nowrap capitalize'>
                                    {user.first_name} {user.last_name}
                                </p>
                                {user?.premium && <IconCustomGoldPremium className='size-5' />}
                            </div>
                            <span className='text-xs text-muted-foreground'>{user.username}</span>
                        </div>
                    </Link>
                </SheetTrigger>
            </div>
            <div className='pl-6 w-full rounded-lg gap-y-1'>
                <div className='flex flex-col w-full gap-1'>
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick}>
                            <Link
                                href='/home'
                                className='flex items-center gap-2 h-12 px-3 text-sm text-center font-light md:mb-0 hover:bg-secondary hover:border-2 hover:text-foreground rounded-md transition-all duration-500 hover:font-semibold'
                            >
                                <DuoLayoutArrange className='inline w-7 h-7 aspect-square' />
                                {texts.playerHub}
                            </Link>
                        </div>
                    </SheetTrigger>
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick}>
                            <Link
                                href='/home/profile'
                                className='flex items-center gap-2 h-12 px-3 text-sm text-center font-light md:mb-0 hover:bg-secondary hover:border-2 hover:text-foreground rounded-md transition-all duration-500 hover:font-semibold'
                            >
                                <DuoSettings1 className='inline w-7 h-7 aspect-square' />
                                {texts.settings}
                            </Link>
                        </div>
                    </SheetTrigger>
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick}>
                            <Link
                                href='/premium'
                                className='flex items-center gap-2 h-12 px-3 text-sm text-center font-light md:mb-0 hover:bg-secondary hover:border-2 hover:text-foreground rounded-md transition-all duration-500 hover:font-semibold'
                            >
                                <IconCustomGoldPremium />
                                <span className='bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-600 bg-clip-text text-transparent font-semibold tracking-wide'>
                                    {texts.premium}
                                </span>
                            </Link>
                        </div>
                    </SheetTrigger>
                    {isAdmin && (
                        <SheetTrigger asChild>
                            <div onClick={onLinkClick}>
                                <Link
                                    href='/admin'
                                    className='flex items-center gap-2 h-12 px-3 text-sm text-center font-light md:mb-0 hover:bg-secondary hover:border-2 hover:text-foreground rounded-md transition-all duration-500 hover:font-semibold'
                                >
                                    <DuoUser className='inline w-7 h-7 aspect-square' /> {texts.admin}
                                </Link>
                            </div>
                        </SheetTrigger>
                    )}
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick}>
                            <Link
                                href='/info/contact-us'
                                className='flex items-center gap-2 h-12 px-3 text-sm text-center font-light md:mb-0 hover:bg-secondary hover:border-2 hover:text-foreground rounded-md transition-all duration-500 hover:font-semibold'
                            >
                                <FaPhoneAlt className='inline size-4 aspect-square' />
                                {texts.contactUs}
                            </Link>
                        </div>
                    </SheetTrigger>
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick}>
                            <Link
                                href='/info/about-us'
                                className='flex items-center gap-2 h-12 px-3 text-sm text-center font-light md:mb-0 hover:bg-secondary hover:border-2 hover:text-foreground rounded-md transition-all duration-500 hover:font-semibold'
                            >
                                <IoInformationCircle className='inline size-6 aspect-square' />
                                {texts.aboutUs}
                            </Link>
                        </div>
                    </SheetTrigger>
                </div>
            </div>
        </div>
    )
}

const AuthButtons = ({
    user,
    texts,
    isHostDomain,
    onLinkClick
}: {
    user: IUser | null
    texts: RightNavTexts
    isHostDomain: boolean
    onLinkClick: () => void
}) => {
    const params = useParams()
    const pathname = usePathname()

    // Extract current language from params or pathname
    const paramsLang = params.lang
    const routeLang = Array.isArray(paramsLang) ? paramsLang[0] : paramsLang
    const langFromPath = extractLangFromPath(pathname || '', 'en')
    const currentLang = routeLang || langFromPath || 'en'

    const handleSignOut = async () => {
        onLinkClick()
        // Use the current language to ensure we stay on the same domain
        await handleLogout({ lang: currentLang })
    }

    return (
        <div className='flex flex-col gap-2'>
            {user ? (
                <div
                    onClick={handleSignOut}
                    className={`w-full  cursor-pointer font-semibold  place-content-center text-center rounded-md h-10 ${isHostDomain ? 'hover:bg-zinc-50 bg-zinc-50 !text-black' : 'bg-primary text-foreground hover:opacity-80'}`}
                >
                    <DuoDoorOpen className='inline w-7 h-7 aspect-square' />
                    {texts.logout}
                </div>
            ) : (
                <>
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick} className='w-full flex'>
                            <Link
                                href='/login'
                                className={`w-full !text-black font-semibold bg-zinc-50 place-content-center text-center rounded-md h-10 cursor-pointer hover:bg-zinc-50`}
                            >
                                {texts.login}
                            </Link>
                        </div>
                    </SheetTrigger>
                    <SheetTrigger asChild>
                        <div onClick={onLinkClick} className='w-full flex'>
                            <Link
                                href='/signup'
                                className={`${isHostDomain ? 'hover:!bg-[#2d3b5c] text-zinc-50 !border-zinc-500 !bg-[#2d3b5c]' : 'bg-muted hover:opacity-80 text-foreground border-muted-foreground'} w-full font-semibold
                                cursor-pointer  backdrop-blur-md place-content-center text-center rounded-md h-10 border-2`}
                            >
                                {texts.signup}
                            </Link>
                        </div>
                    </SheetTrigger>
                </>
            )}
        </div>
    )
}

export default RightNavbarSliderMenu
